import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { boronClient } from "../../api";
import StudentTag from "../../domains/StudentTag";
import TagEntity from "../../entities/TagEntity";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import StudentInterface from "../../interfaces/StudentInterface";

export const useFetchDestinationTagRecipients = ({
  sectionId,
  destinationTag,
}: {
  sectionId: string;
  destinationTag: StudentTag | null;
}) => {
  const query = buildQuery(destinationTag);

  const result = useInfiniteQuery<ApiResponse<StudentInterface[]>, HTTPErrors>({
    queryKey: ["/api/v1/sections/{section_id}/students", sectionId, query],
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(
        "/api/v1/sections/{section_id}/students",
        {
          params: {
            path: {
              section_id: sectionId,
            },
            query: {
              ...query,
              page: pageParam as number,
            },
          },
        },
      );

      if (response.ok && data) {
        return data.students;
      }

      throw await createError(response);
    },
    getNextPageParam,
    initialPageParam: 1,
    enabled: !!destinationTag,
  });

  const recipients = useMemo(
    () => result.data?.pages.flatMap((page) => page.data) || null,
    [result.data],
  );

  return {
    recipients,
    ...result,
  };
};

const buildQuery = (destinationTag: StudentTag | null) => {
  if (!destinationTag) {
    return {};
  }

  const queryParams = new TagEntity(destinationTag).toQueryParams();
  return {
    ...queryParams,
    status: ["active"],
  };
};
